import axios from "axios";
import { atom, selector, selectorFamily } from "recoil";
import { COMMUNICATE_ERROR_MESSAGE_LINKS_FLIGHT_LIST, COMMUNICATE_ERROR_MESSAGE_LINKS_UNLINKED_COPILOT, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../constants/ErrorMessage";
import { formatUTCDate } from "../utils/FPEUtils";
import localStorageEffect from "./effect/LocalStorageEffect";
import tabSyncEffect from "./effect/TabSyncEffect";
import { userState } from "./UserState";

export const getCoPiList = async (userGroup) => {

  const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
  const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
  return axios.get(`${apiUrl}/links/flight_list`, {
    params: {
      usergroup_id: userGroup
    },
    headers: {
      'x-api-key': apiKey
    },
    errorHandle: {
      message: COMMUNICATE_ERROR_MESSAGE_LINKS_FLIGHT_LIST,
      additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
    }
  }).then(res => {
    let data = Object.keys(res.data).map(k => {return {...{aircraft_code: k}, ...res.data[k]}}).sort((a, b) => {
      if (a.aircraft_code > b.aircraft_code) {
        return 1;
      } else {
        return -1;
      }
    });
    return data;
  }).catch(e => {
    console.error('CoPilotList load failed.', e);
    throw e;
  });
}

/**
 * CoPiList
 */
 export const coPiListDataState = atom({
  key: 'coPiListDataState',
  default: selector({
    key: 'coPiListDataStateSelector',
    get: async ({get}) => {
      try {
        const userGroup = get(userState).group;
        return await getCoPiList(userGroup);
      } catch (e) {
        return undefined;
      }
    }
  }),
  effects: [
    tabSyncEffect('coPiList')
  ]
});

export const coPiListLoadFailedState = atom({
  key: 'coPiListLoadFailedState',
  default: false,
  effects: [
    tabSyncEffect('coPiListLoadFailed')
  ]
});

export const coPiListDataStateById = selectorFamily({
  key: 'coPiListDataStateById',
  get: id => ({get}) => {
    const all = get(coPiListDataState);
    return all?.find(f => f.aircraft_code === id);
  }
});

/**
 * CoPiListの選択状態
 */
export const coPiListCheckedState = atom({
  key: 'coPiListCheckedState',
  default: null
});

const alertDebug = false;
export const makeAlertData = (data, prevAlerts) => {
  alertDebug && debugAlertLog(data);
  const date = Date.now();
  const alertList = [];
  data?.forEach(d => {
    const flightPlan = d.flight_plan;
    const currentPlan = flightPlan.find(p => p.is_current_plan);
    if (!currentPlan) {
      const prevAlert = prevAlerts?.find(a => a.aircraft_code === d.aircraft_code);
      alertList.push(makeAlertDataImpl(d, date, null, prevAlert, true));
    }
    flightPlan.forEach(plan => {
      const prevAlert = prevAlerts?.find(a => a.flightplan_id === plan.flightplan_id);
      alertList.push(makeAlertDataImpl(d, date, plan, prevAlert, plan.is_current_plan));
    });
  });
  return alertList;
}

const makeAlertDataImpl = (data, date, plan, prevAlert, represent) => {

  const isCurrentPlan = plan ? plan.is_current_plan : false;
  const ret = {
    // 機体関連
    aircraft_code : data.aircraft_code,
    flightplan_id: plan ? plan.flightplan_id : '',
    is_current_plan: isCurrentPlan,
    // alert status
    alert_on : false,
    confirmed: false,
    useCopiList: represent,
    // alert: 接近
    approach_alert_result : represent && data.approach_alert_result,
    approach_aircraft: represent && data.approach_aircraft,
    // alert: 逸脱
    route_deviation_alert_result: represent && data.route_deviation_alert_result,
    deviation_distance_nm: represent && data.deviation_distance_nm,
    // alert: 交差
    cross_alert_result : plan ? plan.cross_flight_plan.length > 0 : false,
    cross_aircraft_code : plan ? plan.cross_flight_plan : [],
    // alert: 未更新
    unupdated_alert_result : represent && data.unupdated_alert_result,
    unupdated_time_min : represent && data.unupdated_time_min,
    // 機体毎のAlertのON/OFF
    alertSwitch: true
  }

  // 何かしらのAlertが立っていた場合は本データのAlertはON
  ret.alert_on = ret.approach_alert_result
    || ret.route_deviation_alert_result
    || ret.cross_alert_result
    || ret.unupdated_alert_result;

  let newlyAlert = false;
  if (!prevAlert) {
    newlyAlert = ret.alert_on;
  } else {
    // 各種Alertの何れかが立っていない状態から立った状態になった場合は新しいAlert有り
    if ((!prevAlert.approach_alert_result && ret.approach_alert_result)
      || (!prevAlert.route_deviation_alert_result && ret.route_deviation_alert_result)
      || (!prevAlert.cross_alert_result && ret.cross_alert_result)
      || (!prevAlert.unupdated_alert_result && ret.unupdated_alert_result)) {
      newlyAlert = true;
    }
    ret.alertSwitch = prevAlert.alertSwitch;
  }

  if (!newlyAlert) {
    if (prevAlert) {
      // Alertの状態が変わっていない場合、2分たったら確認済みと同じ扱い
      if (prevAlert.alert_reception_time < (date - 2 * 60 * 1000)) {
        ret.confirmed = true;
      } else {
        ret.confirmed = prevAlert.confirmed;
      }
      ret.alert_reception_time = prevAlert.alert_reception_time;
    }
  } else {
    ret.confirmed = false;
    ret.alert_reception_time = date;
  }

  return ret;
}

/**
 * CoPiAlert
 */
export const coPiAlertState = atom({
  key: 'coPiAlertState',
  default: selector({
    key: 'coPiAlertStateSelector',
    get: ({get}) => {
      const coPiListData = get(coPiListDataState);
      return makeAlertData(coPiListData);
    }
  }),
  effects: [
    tabSyncEffect('coPiAlert')
  ]
});

export const coPiAlertStateById = selectorFamily({
  key: 'coPiAlertStateById',
  get: id => ({get}) => {
    const all = get(coPiAlertState);
    return all.find(r => r.aircraft_code === id && r.useCopiList);
  }
});

const debug = false;
export const getUnlinkedCopilot = async (customer) => {
  if (debug) {
    return createDummy();
  }
  const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
  const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
  return axios.get(`${apiUrl}/links/unlinked_copilot`, {
    params: {
      'customer_code': customer
    },
    headers: {
      'x-api-key': apiKey
    },
    errorHandle: {
      message: COMMUNICATE_ERROR_MESSAGE_LINKS_UNLINKED_COPILOT,
      additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
    }
  }).then(res => {
    return res.data;
  }).catch(e => {
    console.error('unlinked_copilot load failed.', e);
    throw e;
  });
}

/**
 * CoPiWithoutRelationState
 */
export const coPiWithoutRelationState = atom({
  key: 'coPiWithoutRelationState',
  default: []
});

/**
 * CoPiAlert
 */
export const coPiLoadingState = atom({
  key: 'coPiLoadingState',
  default: false,
  effects: [
    localStorageEffect('coPiLoading', true)
  ]
});

const createDummy = () => {
  const cnt = 3;
  const ret = [];
  const date = new Date();
  for (let i = 0; i < cnt; i++) {
    const a = Math.floor(Math.random() * 4);
    date.setMinutes(date.getMinutes() + a);
    const dateString = formatUTCDate(date, '%Y-%m-%d %H:%M:%S');

    let data = {
      sbd_imei: `${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}${i}`,
      interval_sec: 120,
      latest_obs_time: null,
      message_time: null,
      message: null
    };
    switch (a) {
      case 0:
        // position
        data.latest_obs_time = dateString;
        break;
      case 1:
        // message
        data.message_time = dateString;
        data.message = `${a}message${a}`;
        break;
      case 2:
        // position + message
        data.latest_obs_time = dateString;
        data.message_time = dateString;
        data.message = `${a}position + message${a}`;
        break;
      default:
        // none
        break;
    }
    ret.push(data);
  }
  return ret;
}

const debugAlertLog = data => {

  console.log(`dLog debugAlertLog: ${new Date()} -----`)
  data.forEach(d => {
    const crossAlertTarget = d.flight_plan.filter(d => d.cross_flight_plan.length > 0);
    if (!d.approach_alert_result && !d.route_deviation_alert_result && !d.unupdated_alert_result && crossAlertTarget.length === 0) {

    } else {
      console.log(`dLog aircraft_code: ${d.aircraft_code}`);
      if (d.approach_alert_result) {
        console.log(`dLog 接近: ${d.approach_aircraft.map(a => a.aircraft_code).join(', ')}`);
      }
      if (d.route_deviation_alert_result) {
        console.log(`dLog 逸脱: ${d.deviation_distance_nm}`);
      }
      if (d.unupdated_alert_result) {
        console.log(`dLog 未更新: ${d.unupdated_time_min}`);  
      }
      d.flight_plan.forEach((p, i) => {
        if (p.cross_flight_plan.length > 0) {
          console.log(`dLog 交差${i}: ${p.cross_flight_plan.map(p => p.aircraft_code).join(', ')}`);
          console.log(`dLog current: ${p.is_current_plan} fp_id: ${p.flightplan_id}`);
        }
      });
      console.log('dLog ---');
    }
  });
}

/**
 * 機体 Source毎のチェック状態
 */
export const coPiSourceCheckedState = atom({
  key: 'coPiSourceCheckedState',
  default: {}
});
